import { useMemo } from "react";
import { useGetDataSourcesQuery } from "store/api";
import {
  joinBaseUrlAndPath,
  normalizeBareNumericEnvPath,
  replaceParams,
  replaceTrailingNumericWithEnvLang,
  usesDecodeParamsEnvSegment,
} from "pages/grid/utils/gridHelpers";

export type DataSourceRecord = Record<string, any>;

export const resolveEndpoint = (rawValue: unknown) => {
  const raw = typeof rawValue === "string" ? rawValue : "";
  const env = localStorage.getItem("_env_id") || "1";
  const lang = localStorage.getItem("_lang") || "fr";

  return raw
    .replaceAll("##env##", env)
    .replaceAll("##lang##", lang)
    .replaceAll("##id##", "1");
};

export const buildWidgetPreviewRequest = (ds: DataSourceRecord | undefined) => {
  if (!ds?.read?.endpoint) return null;

  const rawEndpoint = String(ds.read.endpoint);
  const baseUrl = String(ds.baseUrl ?? "");
  const lang = localStorage.getItem("_lang") || "fr";
  const storedEnv = localStorage.getItem("_env_id") || localStorage.getItem("_env") || "1";
  const envNumeric = Number(storedEnv);
  const hasValidEnv = !Number.isNaN(envNumeric);

  const useComposite =
    usesDecodeParamsEnvSegment(rawEndpoint) ||
    usesDecodeParamsEnvSegment(baseUrl) ||
    /##env##/.test(rawEndpoint);

  const params: Record<string, string | number> = { lang };
  if (hasValidEnv) {
    const envComposite = `env=${envNumeric}&lang=${lang}`;
    params.env = useComposite ? envComposite : String(envNumeric);
  }

  let endpoint = replaceParams(rawEndpoint, params);
  const hasEnvPlaceholder = /\{env\}|:env\b|##env##/.test(rawEndpoint);
  if (!hasEnvPlaceholder && endpoint.endsWith("/") && hasValidEnv) {
    const envComposite = `env=${envNumeric}&lang=${lang}`;
    endpoint = `${endpoint}${useComposite ? envComposite : String(envNumeric)}`;
  }

  if (hasValidEnv) {
    endpoint = replaceTrailingNumericWithEnvLang(baseUrl, endpoint, envNumeric, lang);
    endpoint = normalizeBareNumericEnvPath(endpoint, envNumeric, lang);
  }

  endpoint = endpoint.replace(/\/+$/, "");

  return {
    url: joinBaseUrlAndPath(baseUrl, endpoint),
    method: String(ds?.read?.method ?? "GET"),
    datasourceId: Number(ds?.id ?? 0),
    layoutId: "widget-TableWidgetInput",
  };
};

export const extractPreviewRows = (value: unknown): Record<string, any>[] => {
  if (Array.isArray(value)) return value as Record<string, any>[];

  const nestedData = (value as { data?: unknown } | null)?.data;
  if (Array.isArray(nestedData)) {
    return nestedData as Record<string, any>[];
  }

  return [];
};

export const useDatasourceOptions = () => {
  const { data: dataSources = [] } = useGetDataSourcesQuery("");

  const datasourceOptions = useMemo(
    () =>
      (Array.isArray(dataSources) ? dataSources : []).map((item: DataSourceRecord) => ({
        value: String(item?.id ?? ""),
        text: String(item?.name ?? item?.id ?? ""),
      })),
    [dataSources]
  );

  return { dataSources, datasourceOptions };
};
